import React, { useState } from 'react';
import { Mail, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatINR } from '../lib/gst';
import { downloadPDF, generateInvoicePDF } from '../lib/pdf';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const invoiceAmount = (invoice) => invoice?.taxSummary?.grandTotal || invoice?.grandTotal || 0;
const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export function invoiceShareMessage(invoice) {
  const number = invoice?.number || 'your invoice';
  const business = invoice?.business?.name || 'BillJaldi';
  const customer = invoice?.customer?.name || invoice?.customerName || 'there';
  const amount = formatINR(invoiceAmount(invoice));
  const due = formatDate(invoice?.dueDate);
  const dueText = due ? ` Due date: ${due}.` : '';
  return `Hi ${customer}, sharing invoice ${number} from ${business} for ${amount}.${dueText} Please review and process it when convenient.\n\nPowered by billjaldi.com`;
}

export default function InvoiceShareDialog({ invoice, open, onOpenChange }) {
  const { isPro } = useAuth();
  const { t } = useLanguage();
  const [busyTarget, setBusyTarget] = useState(null);
  if (!invoice) return null;
  const message = invoiceShareMessage(invoice);
  const subject = `Invoice ${invoice.number || ''}`.trim();
  const emailUrl = `mailto:${invoice.customer?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const fileName = `${invoice.number || 'Invoice'}.pdf`;

  const openShareTarget = async (target, url) => {
    setBusyTarget(target);
    try {
      const bytes = await generateInvoicePDF(invoice, { watermark: !isPro });
      if (target === 'native') {
        const file = new File([bytes], fileName, { type: 'application/pdf' });
        if (!navigator.canShare?.({ files: [file] }) || !navigator.share) {
          toast.error(t('nativeShareUnavailable'));
          setBusyTarget(null);
          return;
        }
        await navigator.share({ title: subject, text: message, files: [file] });
        onOpenChange(false);
        return;
      }
      downloadPDF(bytes, fileName);
      toast.info(t('pdfDownloadedAttach'));
    } catch {
      toast.error(t('pdfShareFailed'));
      setBusyTarget(null);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
    setBusyTarget(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invoice-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{t('shareInvoice')}</DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line">
          {message}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            type="button"
            onClick={() => openShareTarget('whatsapp', whatsappUrl)}
            disabled={busyTarget != null}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            data-testid="share-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> {busyTarget === 'whatsapp' ? t('preparing') : 'WhatsApp'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => openShareTarget('email', emailUrl)}
            disabled={busyTarget != null}
            className="font-bold"
            data-testid="share-email-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> {busyTarget === 'email' ? t('preparing') : t('emailWord')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
